import React, { forwardRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  FaEnvelope,
  FaLock,
  FaUser,
  FaEye,
  FaEyeSlash,
  FaArrowRight,
  FaExclamationTriangle,
} from 'react-icons/fa';

const accent = '#00B4D8';
const accentSoft = 'rgba(0, 180, 216, 0.18)';
const danger = '#EF476F';
const mono = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace";
const borderSoft = 'rgba(255, 255, 255, 0.08)';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const orbital = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;


/* ---------- form layout ---------- */

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldOuter = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(229, 240, 250, 0.55);
  animation: ${fadeIn} 0.4s ease both;
`;

const FieldRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${({ $hasError, $focused }) =>
    $hasError ? 'rgba(239, 71, 111, 0.45)' : $focused ? accent : borderSoft};
  border-radius: 7px;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
  overflow: hidden;

  ${({ $focused }) =>
    $focused &&
    css`
      background: rgba(0, 180, 216, 0.03);
      box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.08);
    `}
`;

const FieldIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.7rem;
  color: ${({ $focused }) => ($focused ? accent : 'rgba(229, 240, 250, 0.4)')};
  font-size: 0.85rem;
  border-right: 1px solid ${borderSoft};
  height: 100%;
  transition: color 0.18s ease;
`;

const InputEl = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 0.92rem;
  font-family: inherit;
  padding: 0.85rem 0.85rem;
  letter-spacing: 0.01em;

  &::placeholder {
    color: rgba(229, 240, 250, 0.32);
    font-family: ${mono};
    font-size: 0.82rem;
    letter-spacing: 0.04em;
  }

  /* eliminar autofill amarillo */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: #fff;
    -webkit-box-shadow: 0 0 0 1000px transparent inset;
    transition: background-color 600000s 0s, color 600000s 0s;
    caret-color: ${accent};
  }
`;

const FieldToggle = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  color: rgba(229, 240, 250, 0.4);
  padding: 0 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  height: 100%;
  border-left: 1px solid ${borderSoft};
  transition: color 0.18s ease;

  &:hover { color: ${accent}; }
`;

const ScanLine = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    ${accent},
    rgba(255, 255, 255, 0.85),
    ${accent},
    transparent
  );
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: scaleX(${({ $visible }) => ($visible ? 1 : 0)});
  transform-origin: left;
  transition: transform 0.35s ease, opacity 0.2s ease;
`;

/* ---------- public API: <Field>label<Input/></Field> ---------- */

const ICON_BY_TYPE = {
  email: FaEnvelope,
  password: FaLock,
  text: FaUser,
};

/* La firma esperada por Login/Register: <Field>Label<Input .../></Field> */
export const Field = ({ children, ...rest }) => (
  <FieldOuter {...rest}>{children}</FieldOuter>
);

/* Input "smart": detecta tipo, agrega ícono y estado focused. */
export const Input = forwardRef(function Input(
  { type = 'text', error = false, ...rest },
  ref
) {
  const [focused, setFocused] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const isPassword = type === 'password';
  const realType = isPassword && revealed ? 'text' : type;
  const Icon = ICON_BY_TYPE[type] || FaUser;

  return (
    <FieldRow $hasError={error} $focused={focused}>
      <FieldIcon $focused={focused}>
        <Icon />
      </FieldIcon>
      <InputEl
        ref={ref}
        type={realType}
        onFocus={(e) => {
          setFocused(true);
          rest.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          rest.onBlur?.(e);
        }}
        {...rest}
      />
      {isPassword && (
        <FieldToggle
          aria-label={revealed ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          onClick={() => setRevealed((v) => !v)}
          tabIndex={-1}
        >
          {revealed ? <FaEyeSlash /> : <FaEye />}
        </FieldToggle>
      )}
      <ScanLine $visible={focused} />
    </FieldRow>
  );
});

/* ---------- submit button (minimalista) ---------- */

const ButtonEl = styled.button`
  position: relative;
  margin-top: 0.45rem;
  padding: 0.85rem 1.1rem;
  border-radius: 7px;
  border: 1px solid rgba(0, 180, 216, 0.5);
  color: #ffffff;
  font-weight: 500;
  font-size: 0.86rem;
  font-family: inherit;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(0, 180, 216, 0.1);
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.12s ease;

  svg { transition: transform 0.18s ease; font-size: 0.7rem; }

  &:hover:not(:disabled) {
    background: rgba(0, 180, 216, 0.18);
    border-color: ${accent};
    svg { transform: translateX(3px); }
  }

  &:active:not(:disabled) { transform: translateY(1px); }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SpinnerOrbit = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-top-color: #ffffff;
    animation: ${orbital} 0.9s linear infinite;
  }
`;

export const Button = ({ children, disabled, ...rest }) => {
  const isLoading = typeof children === 'string' && /…|\.\.\./.test(children);
  return (
    <ButtonEl disabled={disabled} {...rest}>
      {isLoading ? <SpinnerOrbit /> : null}
      <span>{children}</span>
      {!isLoading && <FaArrowRight size={12} />}
    </ButtonEl>
  );
};

/* ---------- error / footer ---------- */

export const ErrorText = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin: 0;
  padding: 0.65rem 0.85rem;
  background: rgba(239, 71, 111, 0.08);
  border: 1px solid rgba(239, 71, 111, 0.35);
  border-left: 2px solid ${danger};
  border-radius: 5px;
  color: #ffd0db;
  font-size: 0.82rem;
  line-height: 1.45;
  animation: ${fadeIn} 0.25s ease both;

  &::before {
    content: '!';
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(239, 71, 111, 0.35);
    border: 1px solid ${danger};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${mono};
    font-size: 0.7rem;
    font-weight: 700;
    color: #fff;
    margin-top: 1px;
  }
`;

export const Footer = styled.p`
  margin: 1.4rem 0 0;
  text-align: center;
  font-size: 0.82rem;
  color: rgba(229, 240, 250, 0.45);
  letter-spacing: 0.02em;

  a {
    color: ${accent};
    font-weight: 500;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.18s ease, color 0.18s ease;

    &:hover {
      color: #fff;
      border-bottom-color: ${accent};
    }
  }
`;

/* ---------- agregados (nuevos, opcionales) ---------- */

export const FineLine = styled.div`
  margin: 1.1rem 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(229, 240, 250, 0.32);

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${borderSoft};
  }
`;
